import React, { useEffect, cloneElement } from 'react';
import { FieldValues } from 'react-hook-form';
import ReactDOM from 'react-dom';
import {
  ClickOffEditInlineSx,
  EditInlineBoxSx,
  EditInlineButtonGroupSx,
  EditInlineSx,
} from 'ui-component/EditInline';
import { RenderMutationCellWithInputProps } from 'ui-component/DataGrid/Render/RenderMutationCellWithInput';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { handleErr } from 'utils/functions';
import { EditInlineStringInput } from 'ui-component/HookFormComponents/InlineEditInputComponents';
import useInlineEditCellHookForm from 'ui-component/DataGrid/Cell/useInlineEditCellHookForm';
import useSnackbar from 'hooks/useSnackbar';
import BaseCellWithInput from 'ui-component/DataGrid/Cell/BaseCellWithInput';
import { Paper, Typography } from '@mui/material';
import { useInlineEditClickHandling } from 'hooks/useInlineEditClickHandling';

const MutationCellWithInput = ({
  params,
  InputElement = EditInlineStringInput,
  validation,
  permissionScope,
  useMutation,
  getMutationArgs,
  preSubmit,
  defaultValue,
  additionalInputElementProps,
  tooltip,
  clientV2,
  disableBulkInlineEdit,
  onSuccess,
}: RenderMutationCellWithInputProps) => {
  const [mutate, { isLoading }] = useMutation();

  const handleStopEdit = ({
    id,
    field,
  }: {
    id: GridRenderCellParams['id'];
    field: GridRenderCellParams['field'];
  }) => {
    params.api.stopCellEditMode({ id, field });
  };

  const handleEditCellValue = ({
    id,
    field,
    data,
  }: {
    id: GridRenderCellParams['id'];
    field: GridRenderCellParams['field'];
    data: FieldValues;
  }) => {
    params.api.setEditCellValue({ id, field, value: data[field] });
  };

  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useInlineEditCellHookForm({ params, validation });

  useEffect(() => {
    reset({
      [params.colDef.field]: defaultValue ?? params.value,
    });
  }, [params.value]);

  const { formRef, tempClickOffInlineEdit } = useInlineEditClickHandling({
    handleSubmit,
    onSubmit: () => handleSubmit(onSubmit)(),
  });

  const inlineSx = tempClickOffInlineEdit ? ClickOffEditInlineSx : EditInlineSx;

  const clonedInputElement = cloneElement(InputElement, {
    control,
    name: params.colDef.field,
    label: params.colDef.headerName,
    errors,
    size: 'small',
    sx: { ...inlineSx, width: '250px' },
    boxSx: EditInlineBoxSx,
    buttonGroupSx: EditInlineButtonGroupSx,
    defaultValue,
    ...(additionalInputElementProps || []),
    autoFocus: true,
  });

  const selectedRowData = params.api.getSelectedRows();
  const selectedRowIds = Array.from(selectedRowData.keys()) as string[];
  const willBulkEdit =
    !disableBulkInlineEdit &&
    selectedRowIds.length > 1 &&
    selectedRowIds.includes(params.row.id);

  const onSubmit = async (data: FieldValues) => {
    try {
      const mutationArgs = getMutationArgs
        ? getMutationArgs(data)
        : {
            id: params.id,
          };

      if (clientV2 && willBulkEdit) {
        await mutate(
          selectedRowIds.map((id) => ({
            ...(preSubmit
              ? preSubmit(data, { ...params, id, row: selectedRowData.get(id) })
              : data),
            id,
          }))
        ).unwrap();
      } else {
        const payload = preSubmit ? preSubmit(data, params) : data;
        await mutate(
          clientV2
            ? { id: params.row.id, ...payload }
            : { ...mutationArgs, payload }
        ).unwrap();
      }

      if (params.api.getCellMode(params.id, params.field) === 'edit') {
        handleEditCellValue({ id: params.id, field: params.field, data });
        handleStopEdit({ id: params.id, field: params.field });
      }
      dispatchSuccessSnackbar(`${params.colDef.headerName} updated`);
      onSuccess && onSuccess(params);
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        dispatchErrorSnackbar(
          `Failed to update ${params.colDef.headerName}: ${errMessage}`
        );
      });
    }
  };

  // Get cell position for anchoring the popup
  const cellElement = params.api.getCellElement(params.id, params.field);
  const cellRect = cellElement?.getBoundingClientRect();
  const isPastHalfway = cellRect
    ? cellRect.left > window.innerWidth / 2
    : false;

  // Create a portal for the form
  const formContent = (
    <Paper
      elevation={3}
      sx={{
        padding: 1,
        zIndex: 1000, // Very high z-index
        position: 'fixed',
        top: cellRect ? `${cellRect.top}px` : '50%',
        left: !isPastHalfway
          ? cellRect
            ? `${cellRect.left}px`
            : '50%'
          : undefined,
        right: isPastHalfway
          ? cellRect
            ? `${window.innerWidth - cellRect.right}px`
            : '50%'
          : undefined,
        transform: cellRect ? 'none' : 'translate(-50%, -50%)',
        maxWidth: '90vw',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <BaseCellWithInput
          clonedInputElement={clonedInputElement}
          isLoading={isLoading}
          permissionScope={permissionScope}
          handleStopEdit={() =>
            handleStopEdit({ id: params.id, field: params.field })
          }
          tooltip={tooltip}
        />
      </form>
      {clientV2 && willBulkEdit && (
        <Typography>
          This change will apply to {selectedRowIds.length} rows
        </Typography>
      )}
    </Paper>
  );

  // Return a div for the cell and portal the form to the document body
  return <div>{ReactDOM.createPortal(formContent, document.body)}</div>;
};

export default MutationCellWithInput;
